import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'

interface Patient {
  id: string
  patient_id: string
  full_name: string
  date_of_birth: string
  gender: string
  phone: string
}

interface Slide {
  id: string
  slide_id: string
  name: string
  patient_id: string
  created_at: string
}

interface PatientForReporting {
  patient: Patient
  slides: Slide[]
  first_slide_id: string
  slide_count: number
  latest_upload: string
}

const ReportingTab: React.FC = () => {
  const [patientsForReporting, setPatientsForReporting] = useState<PatientForReporting[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchPatientsForReporting()
  }, [])

  const fetchPatientsForReporting = async () => {
    setLoading(true)
    try {
      // Fetch patients, slides, and reports
      const [patientsRes, slidesRes, histoReportsRes, cytoReportsRes] = await Promise.all([
        fetch('/api/v1/pathology/patients/?page_size=1000'),
        fetch('/api/v1/imaging/slides/?page_size=1000'),
        fetch('/api/v1/pathology/reports/?page_size=1000'),
        fetch('/api/v1/pathology/cytology-reports/?page_size=1000')
      ])

      if (!patientsRes.ok) {
        throw new Error('Failed to fetch data')
      }

      const [patientsData, slidesData, histoReportsData, cytoReportsData] = await Promise.all([
        patientsRes.json(),
        slidesRes.ok ? slidesRes.json() : Promise.resolve({ results: [] }),
        histoReportsRes.ok ? histoReportsRes.json() : Promise.resolve({ results: [] }),
        cytoReportsRes.ok ? cytoReportsRes.json() : Promise.resolve({ results: [] })
      ])

      const allPatients = patientsData.results || patientsData
      const slides = (slidesData.results || slidesData) || []
      const histoReports = (histoReportsData.results || histoReportsData) || []
      const cytoReports = (cytoReportsData.results || cytoReportsData) || []

      // Group slides by patient
      const slidesByPatient = new Map<string, Slide[]>()
      slides.forEach((slide: any) => {
        if (slide.patient_id) {
          const patientId = slide.patient_id.toString()
          if (!slidesByPatient.has(patientId)) {
            slidesByPatient.set(patientId, [])
          }
          slidesByPatient.get(patientId)!.push(slide)
        }
      })

      // Get patients with reports
      const patientsWithReports = new Set<string>()
      histoReports.forEach((r: any) => {
        if (r.patient) patientsWithReports.add(r.patient.toString())
      })
      cytoReports.forEach((r: any) => {
        if (r.patient) patientsWithReports.add(r.patient.toString())
      })

      // Filter: patients with slides but no reports
      const patientsNeedingReports: PatientForReporting[] = []

      allPatients.forEach((patient: Patient) => {
        const patientId = patient.id.toString()
        const patientSlides = slidesByPatient.get(patientId) || []

        // Has slides but no report
        if (patientSlides.length > 0 && !patientsWithReports.has(patientId)) {
          const sortedSlides = patientSlides.sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )

          patientsNeedingReports.push({
            patient,
            slides: sortedSlides,
            first_slide_id: sortedSlides[0].id,
            slide_count: patientSlides.length,
            latest_upload: sortedSlides[0].created_at
          })
        }
      })

      // Sort by latest upload
      patientsNeedingReports.sort((a, b) =>
        new Date(b.latest_upload).getTime() - new Date(a.latest_upload).getTime()
      )

      setPatientsForReporting(patientsNeedingReports)
    } catch (error) {
      console.error('Error fetching reporting data:', error)
      toast.error('Không thể tải dữ liệu tạo phiếu')
    } finally {
      setLoading(false)
    }
  }

  const filteredPatients = patientsForReporting.filter(pf =>
    pf.patient.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pf.patient.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pf.patient.phone.includes(searchTerm)
  )

  return (
    <div>
      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Tìm theo mã BN, họ tên, số điện thoại..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Stats */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Tổng số: <span className="font-semibold text-gray-900">{filteredPatients.length}</span> bệnh nhân cần tạo phiếu
        </div>
        <button
          onClick={fetchPatientsForReporting}
          className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
        >
          🔄 Làm mới
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mã BN
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Họ và Tên
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Số ảnh
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày upload
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : filteredPatients.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  {searchTerm ? 'Không tìm thấy bệnh nhân' : 'Tất cả bệnh nhân đã có phiếu 🎉'}
                </td>
              </tr>
            ) : (
              filteredPatients.map((pf) => (
                <tr key={pf.patient.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {pf.patient.patient_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {pf.patient.full_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {pf.slide_count} ảnh
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(pf.latest_upload).toLocaleDateString('vi-VN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <Link
                      to={`/imaging/viewer/${pf.first_slide_id}`}
                      className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      👁️ Xem ảnh
                    </Link>
                    <Link
                      to="/pathology/reports"
                      className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-medium"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      📝 Tạo phiếu
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ReportingTab
